import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useBranding } from "../../providers/BrandingProvider";
import "./AppFooter.css";

/**
 * Slim global footer — platform name + © year + small links. Renders
 * across the standard app shell (Home, Projects list, Organization,
 * Platform, Stats, etc.) so every page carries a consistent baseline
 * of identity / copyright. Hidden on:
 *
 *   - ``/projects/:id/data`` (Data Manager): every pixel matters
 *     for the table + side panel.
 *   - ``/projects/:id/label`` and ``/projects/:id/stream`` (labeling
 *     surfaces): full-screen LSF stage.
 *   - Auth pages (``/auth/*``, ``/user/*``): rendered outside the
 *     shell entirely, so this never even mounts there.
 *
 * Slim by design — height ~32px, neutral background, body-small
 * font. The branding name comes from the ``BrandingProvider`` so a
 * renamed platform automatically propagates here.
 */

const HIDE_PATTERNS: RegExp[] = [
  /^\/projects\/\d+\/data(\/|$)/,
  /^\/projects\/\d+\/label(\/|$)/,
  /^\/projects\/\d+\/stream(\/|$)/,
];

const shouldHide = (pathname: string): boolean => HIDE_PATTERNS.some((re) => re.test(pathname));

export const AppFooter = () => {
  const location = useLocation();
  const branding = useBranding();
  const hidden = shouldHide(location.pathname);
  // Tell the layout when the fixed footer is present so the content wrapper
  // can reserve bottom space app-wide (every page gets a genuine gap before
  // the footer). Removed on full-bleed pages where the footer is hidden.
  useEffect(() => {
    document.body.classList.toggle("app-has-footer", !hidden);
    return () => document.body.classList.remove("app-has-footer");
  }, [hidden]);
  if (hidden) return null;
  const year = new Date().getFullYear();
  const name = branding?.name || "AfriAnnotate";
  // Portal to <body>: the footer is position:fixed, and on iOS WKWebView
  // a fixed element whose ancestor is a scroll container with
  // overscroll-behavior:contain gets its containing block miscomputed
  // and lands BELOW the visible viewport (Android was fine — the
  // "no footer on iPhone" report). Rendering at the body level makes
  // the viewport its containing block on every engine.
  if (typeof document === "undefined") return null;
  return createPortal(
    <footer className="app-footer" role="contentinfo">
      <span className="app-footer__copy">
        © {year} {name}
      </span>
      <span className="app-footer__sep" aria-hidden />
      <span className="app-footer__tagline">All rights reserved.</span>
      {branding?.website_url && (
        <>
          <span className="app-footer__sep" aria-hidden />
          {/* Native: the global capture-phase handler in helpers.ts
              (installExternalLinkHandler) routes these through the
              in-app browser — no per-link onClick, which would
              double-open alongside it. */}
          <a className="app-footer__link" href={branding.website_url} target="_blank" rel="noreferrer">
            Website
          </a>
        </>
      )}
      {branding?.docs_url && (
        <>
          <span className="app-footer__sep" aria-hidden />
          <a className="app-footer__link" href={branding.docs_url} target="_blank" rel="noreferrer">
            Docs
          </a>
        </>
      )}
      {branding?.privacy_url && (
        <>
          <span className="app-footer__sep" aria-hidden />
          <a className="app-footer__link" href={branding.privacy_url} target="_blank" rel="noreferrer">
            Privacy
          </a>
        </>
      )}
    </footer>,
    document.body,
  );
};
